/**
 * Amplifier profiles -- group 12 (headphone drive, including electrostatic).
 *
 * Six of these eight exist to drive a transducer that is measured in milliwatts,
 * and the interesting engineering differences between them are output impedance,
 * rail voltage and where the distortion sits, not headline power. Two are
 * direct-drive electrostatic energisers with deliberately opposite
 * implementations -- valve output versus all-FET -- so the pair should not
 * compile to the same DSP. Published figures are tagged as such; several of
 * these builders are small shops that publish nothing, which is stated.
 */

import type { AmpProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeAmp, usd } from '../kit';

export const AMPS_12: AmpProfile[] = [
  makeAmp({
    mfr: 'HeadAmp',
    model: 'Blue Hawaii SE',
    price: usd(6500, 'APPROXIMATE', 2023, 'Approximate US retail; built to order and priced by chassis, transformer and tube options.'),
    year: 2007,
    country: 'United States',
    topology: 'hybrid',
    form: 'electrostatic-energiser',
    devices: '4 x EL34 output valves in a balanced direct-drive stage, solid-state input and current sources, no output transformer',
    power: { biasVolts: 580, outputVrms: 500, confidence: 'ESTIMATED' },
    gainDb: 56,
    thdPercent: 0.1,
    snrDb: 100,
    bandwidth: [5, 60000],
    outputImpedanceOhms: 1200,
    feedbackDb: 6,
    outputTransformer: false,
    electrostaticDrive: true,
    estatBias: 'pro-580v',
    balancedInput: true,
    crosstalkDb: -72,
    harmonicShape: HARMONIC_SHAPES.otl,
    nonlinear: { kind: 'pentode', amount: 0.5, asymmetry: 0.3, bias: 0.35, headroomDb: 12, oversample: 8 },
    dynamics: { sagDb: 0.9, sagTimeMs: 190, recoveryMs: 420, slewRateVus: 30, headroomDb: 1.6 },
    clipping: { kind: 'soft', kneeDb: 7, stickiness: 0.28, onsetDbFS: -1 },
    confidence: 'ESTIMATED',
    accent: '#5aa8d6',
    tags: ['electrostatic', 'energiser', 'stax-pro-bias', 'valve-output', 'balanced'],
    notes:
      'Valve output devices swinging several hundred volts straight onto the stator with a solid-state front end doing the voltage gain and current setting, which is why the harmonic structure is push-pull-like rather than single-ended: partial even-order cancellation leaves third harmonic dominant with a smaller second. Load is capacitive rather than resistive, so the very high source impedance combines with the headphone capacitance to define the high-frequency corner rather than the amplifier bandwidth alone. Supply sag on sustained peaks is modelled as real but modest, since the front end is regulated.',
    provenanceNote:
      'Bias standard, valve complement and transformerless direct-drive topology from HeadAmp published material. HeadAmp does not publish THD, signal-to-noise or output swing figures for this amplifier, so those values are estimates typical of hybrid direct-drive energisers; the price is approximate because the unit is configured to order.',
  }),

  makeAmp({
    mfr: 'Mjolnir Audio',
    model: 'KGSSHV Carbon',
    price: usd(5500, 'APPROXIMATE', 2023, 'Approximate US retail; built to order in small batches, so pricing moves with parts availability.'),
    year: 2014,
    country: 'Iceland',
    topology: 'solid-state-a',
    form: 'electrostatic-energiser',
    devices: 'All-FET direct-drive output with silicon-carbide devices and cascoded current sources, no output transformer',
    power: { biasVolts: 580, outputVrms: 600, confidence: 'ESTIMATED' },
    gainDb: 60,
    thdPercent: 0.02,
    snrDb: 106,
    bandwidth: [1.5, 120000],
    outputImpedanceOhms: 900,
    feedbackDb: 16,
    outputTransformer: false,
    electrostaticDrive: true,
    estatBias: 'pro-580v',
    balancedInput: true,
    crosstalkDb: -88,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'jfet-square', amount: 0.3, asymmetry: 0.25, bias: 0.4, headroomDb: 14, oversample: 4 },
    dynamics: { sagDb: 0.15, sagTimeMs: 40, recoveryMs: 110, thermalDb: 0.3, thermalTimeMs: 7000, slewRateVus: 70, headroomDb: 2.2 },
    clipping: { kind: 'asymmetric', kneeDb: 3, stickiness: 0.06, onsetDbFS: -1 },
    confidence: 'ESTIMATED',
    accent: '#9fb3c8',
    tags: ['electrostatic', 'energiser', 'stax-pro-bias', 'all-fet', 'class-a'],
    notes:
      'The direct comparison case for the valve energiser above: same bias standard, same absence of an output transformer, entirely different error mechanism. A class-A FET output with meaningful loop feedback puts the distortion floor an order of magnitude lower and keeps it low-order and fast-decaying, while the higher rail headroom pushes clipping further out and makes the onset harder rather than progressive. Thermal drift replaces supply sag as the slow dynamic term, since the output devices idle hot and the bias moves with temperature.',
    provenanceNote:
      'Topology, silicon-carbide output devices and pro-bias compatibility from Mjolnir Audio published build descriptions. No THD, noise or output-swing specifications are published for this amplifier; all electrical figures are estimates typical of the KGSSHV lineage. Price is approximate and build-dependent.',
  }),

  makeAmp({
    mfr: 'Eddie Current',
    model: 'Zana Deux SE',
    price: usd(3300, 'APPROXIMATE', 2016, 'Approximate US retail when in production; hand-built in small numbers and now hard to buy new.'),
    year: 2012,
    country: 'United States',
    topology: 'transformer-coupled',
    form: 'headphone',
    devices: '6C33C-B single-ended output triode, valve driver stage, output transformer coupled',
    power: { mw32: 300, mw300: 1100, mw600: 900, confidence: 'ESTIMATED' },
    gainDb: 18,
    thdPercent: 0.6,
    snrDb: 88,
    bandwidth: [12, 38000],
    outputImpedanceOhms: 25,
    feedbackDb: 3,
    outputTransformer: true,
    transformerLfHz: 16,
    transformerHfHz: 34000,
    crosstalkDb: -62,
    harmonicShape: HARMONIC_SHAPES.set,
    nonlinear: { kind: 'triode', amount: 0.8, asymmetry: 0.75, bias: 0.85, headroomDb: 8, oversample: 8 },
    dynamics: { sagDb: 1.5, sagTimeMs: 200, recoveryMs: 520, slewRateVus: 16, headroomDb: 1.0 },
    clipping: { kind: 'soft', kneeDb: 8, stickiness: 0.38, onsetDbFS: -1 },
    confidence: 'ESTIMATED',
    accent: '#c46a3c',
    tags: ['single-ended', 'output-transformer', '6c33c', 'high-output-impedance'],
    notes:
      'Single-ended triode operation through a wound output transformer, which stacks two distinct nonlinearities: a strongly asymmetric valve transfer curve producing dominant second harmonic that rises about one dB per dB of drive, and core saturation that lifts low-order distortion specifically at low frequencies. A 25-ohm source impedance is the parameter that matters most in use, because it is high enough to print any load impedance variation directly onto the frequency response, which is a completely different failure mode from the sub-ohm solid-state entries in this group.',
    provenanceNote:
      'Output valve, single-ended transformer-coupled topology and general power class from Eddie Current published material and owner documentation. Eddie Current publishes no distortion, noise or bandwidth specifications, so every electrical figure here is an estimate typical of 6C33C single-ended designs. Price is approximate.',
  }),

  makeAmp({
    mfr: 'Auris Audio',
    model: 'Headonia 2A3',
    price: usd(8000, 'APPROXIMATE', 2022, 'Approximate US retail for the 2A3 version; the 300B version lists higher.'),
    year: 2016,
    country: 'Serbia',
    topology: 'tube-set',
    form: 'headphone',
    devices: '2 x 2A3 directly-heated triode output, 6SN7 driver, multi-tap output transformers',
    power: { mw32: 600, mw300: 2000, mw600: 1600, confidence: 'ESTIMATED' },
    gainDb: 22,
    thdPercent: 1.0,
    snrDb: 82,
    bandwidth: [15, 30000],
    outputImpedanceOhms: 8,
    feedbackDb: 0,
    outputTransformer: true,
    transformerLfHz: 18,
    transformerHfHz: 27000,
    crosstalkDb: -56,
    harmonicShape: HARMONIC_SHAPES.set,
    nonlinear: { kind: 'triode', amount: 0.95, asymmetry: 0.85, bias: 0.9, headroomDb: 7, oversample: 8 },
    dynamics: { sagDb: 2.0, sagTimeMs: 250, recoveryMs: 620, slewRateVus: 12, headroomDb: 0.8 },
    clipping: { kind: 'soft', kneeDb: 9, stickiness: 0.42, onsetDbFS: -1 },
    confidence: 'ESTIMATED',
    accent: '#e0a24a',
    tags: ['directly-heated-triode', '2a3', 'zero-feedback', 'multi-tap'],
    notes:
      'Directly-heated triode output with no global feedback, which is the highest-distortion entry in this group by roughly two orders of magnitude and is deliberately modelled that way rather than being flattered. The distortion is almost entirely second harmonic at low drive with a fast-decaying tail, so total percentage understates how benign the spectrum is; the interesting behaviour is that it never sits at a fixed floor, it tracks level. Selectable transformer taps mean the effective source impedance is chosen by the user, and the value here represents a low-impedance tap.',
    provenanceNote:
      'Output valve type, driver stage, zero-feedback topology and tap arrangement from Auris Audio published material. Auris does not publish THD or signal-to-noise for this model, so those figures are estimates typical of zero-feedback 2A3 single-ended designs; output power per load is likewise estimated from the rated output power rather than quoted per impedance.',
  }),

  makeAmp({
    mfr: 'Feliks Audio',
    model: 'Euforia',
    price: usd(2500, 'APPROXIMATE', 2021, 'Approximate US retail; Polish list pricing in EUR converts differently by market and revision.'),
    year: 2017,
    country: 'Poland',
    topology: 'tube-otl',
    form: 'headphone',
    devices: '6N13S double-triode output in an output-transformerless configuration, 6SN7 driver',
    power: { mw32: 110, mw300: 320, mw600: 250, confidence: 'ESTIMATED' },
    gainDb: 16,
    thdPercent: 0.35,
    snrDb: 90,
    bandwidth: [8, 65000],
    outputImpedanceOhms: 11,
    feedbackDb: 3,
    outputTransformer: false,
    crosstalkDb: -66,
    harmonicShape: HARMONIC_SHAPES.otl,
    nonlinear: { kind: 'triode', amount: 0.65, asymmetry: 0.5, bias: 0.5, headroomDb: 9, oversample: 8 },
    dynamics: { sagDb: 1.1, sagTimeMs: 170, recoveryMs: 400, slewRateVus: 20, headroomDb: 1.3 },
    clipping: { kind: 'soft', kneeDb: 7.5, stickiness: 0.32, onsetDbFS: -1 },
    confidence: 'ESTIMATED',
    accent: '#b08b6a',
    tags: ['otl', '6n13s', 'high-impedance-loads', 'no-transformer'],
    notes:
      'Removing the output transformer removes the low-frequency core saturation and the high-frequency leakage roll-off, so bandwidth extends further at both ends than the transformer-coupled valve entries here, but the price is paid in load sensitivity. Available current is low, so the modelled power falls sharply into 32 ohms while remaining usable into 300 and 600 ohms, and the distortion structure is third-harmonic dominant with a substantial second, which is what an unbalanced cathode-follower output produces. Output impedance around 11 ohms means low-impedance planar loads will see both level loss and response modification.',
    provenanceNote:
      'Valve complement and output-transformerless topology from Feliks Audio published material. Published power and distortion figures are not stated per load impedance for this model, so the per-impedance power values and the THD figure here are estimates typical of 6N13S OTL designs. Price is approximate.',
  }),

  makeAmp({
    mfr: 'Violectric',
    model: 'V281',
    price: usd(1899, 'APPROXIMATE', 2020, 'Approximate US retail; the V281 was sold at varying USD prices against a EUR list.'),
    year: 2014,
    country: 'Germany',
    topology: 'solid-state-ab',
    form: 'headphone',
    devices: 'Discrete complementary output with paralleled high-current buffers, switchable input pre-gain',
    power: { mw32: 2200, mw300: 900, mw600: 480, confidence: 'ESTIMATED' },
    gainDb: 20,
    thdPercent: 0.001,
    snrDb: 112,
    bandwidth: [1, 200000],
    outputImpedanceOhms: 0.4,
    feedbackDb: 34,
    outputTransformer: false,
    balancedInput: true,
    crosstalkDb: -95,
    harmonicShape: HARMONIC_SHAPES.highFeedback,
    nonlinear: { kind: 'class-b-crossover', amount: 0.25, crossoverWidth: 0.002, headroomDb: 14, asymmetry: 0.1, oversample: 4 },
    dynamics: { sagDb: 0.2, sagTimeMs: 35, recoveryMs: 90, thermalDb: 0.1, thermalTimeMs: 6000, slewRateVus: 60, headroomDb: 2.4 },
    clipping: { kind: 'asymmetric', kneeDb: 2, stickiness: 0.06, onsetDbFS: -1 },
    confidence: 'ESTIMATED',
    accent: '#5f7f8f',
    tags: ['high-feedback', 'low-output-impedance', 'pre-gain', 'current-capable'],
    notes:
      'High loop gain around a current-capable output stage, so the residual distortion is very low but what remains is higher-order crossover residue rather than the fast-decaying second-harmonic tail of the valve entries; percentage figures alone would hide that difference, which is why the harmonic distribution is set explicitly. The 0.4-ohm source impedance makes the load impedance curve essentially irrelevant to frequency response, the opposite behaviour to the transformer-coupled and OTL entries. Power delivery is current-limited rather than voltage-limited, so it peaks into low-impedance loads and falls off into 600 ohms.',
    provenanceNote:
      'Topology, pre-gain arrangement and the very low quoted THD and output impedance follow Violectric published specifications. Power per load impedance is estimated from the published output power rather than quoted individually, and the harmonic distribution is inferred from the feedback topology, not measured. Price is approximate.',
  }),

  makeAmp({
    mfr: 'SPL',
    model: 'Phonitor x',
    price: usd(2200, 'APPROXIMATE', 2022, 'Approximate US retail for the Phonitor x without the optional DAC768 converter module.'),
    year: 2017,
    country: 'Germany',
    topology: 'solid-state-ab',
    form: 'headphone',
    devices: 'VOLTAiR discrete operational amplifiers on 120 V rails, analog Phonitor Matrix crossfeed network',
    power: { mw32: 1000, mw300: 3000, mw600: 2000, confidence: 'ESTIMATED' },
    gainDb: 18,
    thdPercent: 0.0011,
    snrDb: 116,
    bandwidth: [10, 200000],
    outputImpedanceOhms: 0.18,
    feedbackDb: 30,
    outputTransformer: false,
    balancedInput: true,
    crosstalkDb: -90,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'class-b-crossover', amount: 0.18, crossoverWidth: 0.0015, headroomDb: 20, asymmetry: 0.08, oversample: 4 },
    dynamics: { sagDb: 0.1, sagTimeMs: 30, recoveryMs: 80, thermalDb: 0.08, thermalTimeMs: 6000, slewRateVus: 90, headroomDb: 6.0 },
    clipping: { kind: 'asymmetric', kneeDb: 1.5, stickiness: 0.05, onsetDbFS: -1 },
    confidence: 'ESTIMATED',
    accent: '#d24b4b',
    tags: ['high-voltage-rails', 'voltair', 'crossfeed-matrix', 'studio'],
    notes:
      'The defining parameter is rail voltage rather than current: running the gain stages from a 120 V supply puts the clipping ceiling roughly 20 dB above where a conventional 15 V design sits, so at normal headphone levels every stage operates a long way from its limits and the working distortion spectrum stays low-order. That inverts the power profile relative to the Violectric entry, delivering most into high-impedance loads and least into 32 ohms. The analog matrix that sets speaker angle and crossfeed is a real inter-channel network, which is why the modelled channel behaviour is not simply a fixed separation figure.',
    provenanceNote:
      'Rail voltage, discrete operational amplifier topology, distortion and signal-to-noise figures follow SPL published specifications for the Phonitor series; output power per load impedance is estimated from the published output voltage rather than quoted per impedance. The harmonic distribution reflects operation far below the rails and is inferred, not measured.',
  }),

  makeAmp({
    mfr: 'Bakoon International',
    model: 'AMP-13R',
    price: usd(9000, 'APPROXIMATE', 2021, 'Approximate US retail; distributor pricing has varied.'),
    year: 2018,
    country: 'South Korea',
    topology: 'solid-state-a',
    form: 'integrated',
    devices: 'SATRI current-mode gain cell with class-A output, no global voltage feedback',
    power: { watts8: 25, watts4: 45, confidence: 'ESTIMATED' },
    gainDb: 26,
    thdPercent: 0.1,
    snrDb: 95,
    bandwidth: [2, 400000],
    outputImpedanceOhms: 1.1,
    feedbackDb: 0,
    outputTransformer: false,
    crosstalkDb: -80,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'jfet-square', amount: 0.45, asymmetry: 0.45, bias: 0.6, headroomDb: 11, oversample: 4 },
    dynamics: { sagDb: 0.4, sagTimeMs: 50, recoveryMs: 130, thermalDb: 0.35, thermalTimeMs: 8000, slewRateVus: 120, headroomDb: 1.5 },
    clipping: { kind: 'asymmetric', kneeDb: 4, stickiness: 0.1, onsetDbFS: -1 },
    confidence: 'ESTIMATED',
    accent: '#7ec8a9',
    tags: ['satri', 'current-mode', 'zero-global-feedback', 'wide-bandwidth', 'current-drive-output'],
    notes:
      'Current-mode gain with no global voltage feedback, which produces two measurable consequences that this profile keeps: a small-signal bandwidth extending far above the audio band, and an output impedance around one ohm rather than the milliohms a feedback-corrected design achieves. That damping factor of roughly seven means the loudspeaker impedance curve is partly transferred onto the frequency response, so this amplifier and a high-feedback design will not sound alike on the same panel or ported loudspeaker even though both measure as low-distortion. Distortion is low-order and level-tracking rather than a fixed floor.',
    provenanceNote:
      'Current-mode SATRI topology, absence of global feedback and rated power from Bakoon published material; the SATRI circuit lineage originates with Bakoon Products of Japan. THD, signal-to-noise, output impedance and bandwidth are estimates typical of this topology rather than quoted specifications, and the price is approximate.',
  }),
];
